import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Form, Button } from 'react-bootstrap';
import { FaMapMarkerAlt, FaEnvelope, FaPaperPlane } from 'react-icons/fa';
import EditableText from '../components/EditableText';

const FREE_EMAIL_DOMAINS = [
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 
  'live.com', 'msn.com', 'yahoo.com', 'yahoo.se', 'yahoo.co.uk',
  'mail.com', 'protonmail.com', 'icloud.com', 'aol.com', 'zoho.com',
  'yandex.com', 'yandex.se', 'gmx.com', 'gmx.se', 'fastmail.com'
];

function generateCaptcha() {
  const a = Math.floor(Math.random() * 10) + 1;
  const b = Math.floor(Math.random() * 10) + 1;
  return { question: `${a} + ${b} = ?`, answer: a + b };
}

function Contact() {
  const [content, setContent] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [captchaInput, setCaptchaInput] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    fetch('/api/content?page=contact')
      .then(res => res.json())
      .then(data => setContent(data))
      .catch(() => {});
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (name === 'email') {
      const domain = value.split('@')[1]?.toLowerCase();
      if (domain && FREE_EMAIL_DOMAINS.includes(domain)) {
        setEmailError('Please use your business email address. Free email providers are not accepted.');
      } else {
        setEmailError('');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (parseInt(captchaInput) !== captcha.answer) {
      setError('Incorrect answer. Please try the math problem again.');
      setCaptcha(generateCaptcha());
      setCaptchaInput('');
      return;
    }
    
    if (emailError) return;
    
    setLoading(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to send message. Please try again.');
      }
    } catch (err) {
      setError('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | 6G AI Sweden</title>
        <meta name="description" content="Contact 6G AI Sweden for AI cloud solutions. Reach out for GPU rental, AI services, or partnership inquiries." />
        <meta name="keywords" content="contact 6G AI, AI cloud contact, Sweden AI services, GPU rental inquiry" />
        <meta property="og:title" content="Contact Us | 6G AI Sweden" />
        <meta property="og:description" content="Get in touch for AI cloud solutions in Sweden." />
      </Helmet>

      {/* Page Header */}
      <section className="page-header">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="page-title"><EditableText section="page_title" page="contact" defaultValue="Contact Us" as="span" /></h1>
              <p className="page-subtitle">
                <EditableText section="page_subtitle" page="contact" defaultValue="Ready to accelerate your AI journey? Get in touch with our team." as="span" />
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Section */}
      <section className="section">
        <Container>
          <Row>
            <Col lg={7} className="mb-5 mb-lg-0">
              {submitted ? (
                <div style={{ 
                  background: 'var(--md-sys-color-surface-container)', 
                  borderRadius: 'var(--radius-large)', 
                  padding: '3rem',
                  border: '1px solid var(--md-sys-color-tertiary)',
                  textAlign: 'center'
                }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px', 
                    borderRadius: '50%', 
                    background: 'var(--md-sys-color-tertiary-container)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1.5rem'
                  }}>
                    <FaPaperPlane size={32} style={{ color: 'var(--md-sys-color-on-tertiary-container)' }} />
                  </div>
                  <h3 style={{ marginBottom: '1rem' }}>Message Sent!</h3>
                  <p style={{ color: 'var(--md-sys-color-on-surface-variant)', marginBottom: 0 }}>
                    Thank you for reaching out. Our team will get back to you within 24 hours.
                  </p>
                </div>
              ) : (
                <div className="contact-form">
                  <h2 style={{ marginBottom: '0.5rem' }}><EditableText section="form_title" page="contact" defaultValue="Send us a Message" as="span" /></h2>
                  <p style={{ color: 'var(--md-sys-color-on-surface-variant)', marginBottom: '2rem' }}>
                    <EditableText section="form_subtitle" page="contact" defaultValue="Fill out the form below and we'll be in touch soon." as="span" />
                  </p>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">Email Address *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        placeholder="john@company.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        isInvalid={!!emailError}
                      />
                      {emailError && (
                        <Form.Control.Feedback type="invalid">
                          {emailError}
                        </Form.Control.Feedback>
                      )}
                      <Form.Text className="text-muted" style={{ fontSize: '0.8rem' }}>
                        Business email required. We do not accept free email providers.
                      </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">Company</Form.Label>
                      <Form.Control
                        type="text"
                        name="company"
                        placeholder="Your Company"
                        value={formData.company}
                        onChange={handleChange}
                      />
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">Message *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={5}
                        name="message"
                        placeholder="Tell us about your AI infrastructure needs..."
                        value={formData.message}
                        onChange={handleChange}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">Security Check *</Form.Label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <span style={{ fontWeight: 600, fontSize: '1.1rem' }}>{captcha.question}</span>
                        <Form.Control
                          type="number"
                          placeholder="Answer"
                          value={captchaInput}
                          onChange={(e) => setCaptchaInput(e.target.value)}
                          style={{ maxWidth: '100px', WebkitAppearance: 'none', MozAppearance: 'textfield' }}
                          required
                        />
                        <Button 
                          variant="outline-secondary" 
                          size="sm"
                          onClick={() => {
                            setCaptcha(generateCaptcha());
                            setCaptchaInput('');
                          }}
                        >
                          ↻
                        </Button>
                      </div>
                    </Form.Group>
                    {error && (
                      <div className="alert alert-danger mb-4" role="alert">
                        {error}
                      </div>
                    )}
                    <Button 
                      variant="primary" 
                      type="submit" 
                      size="lg" 
                      className="w-100"
                      disabled={loading || !!emailError}
                    >
                      {loading ? 'Sending...' : 'Send Message'}
                    </Button>
                  </Form>
                </div>
              )}
            </Col>
            <Col lg={5}>
              <div style={{ 
                background: 'var(--md-sys-color-surface-container)', 
                borderRadius: 'var(--radius-large)', 
                padding: '2rem',
                border: '1px solid var(--md-sys-color-outline-variant)',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{ marginBottom: '1.5rem' }}><EditableText section="contact_info_title" page="contact" defaultValue="Contact Information" as="span" /></h4>
                <div className="infra-feature">
                  <div className="infra-icon"><FaMapMarkerAlt /></div>
                  <div className="infra-content">
                    <h4><EditableText section="contact_location_title" page="contact" defaultValue="Location" as="span" /></h4>
                    <p><EditableText section="contact_location" page="contact" defaultValue="Kista, Stockholm, Sweden" as="span" /></p>
                  </div>
                </div>
                <div className="infra-feature">
                  <div className="infra-icon"><FaEnvelope /></div>
                  <div className="infra-content">
                    <h4><EditableText section="contact_email_title" page="contact" defaultValue="Email" as="span" /></h4>
                    <p><EditableText section="contact_email" page="contact" defaultValue="info@6gai.se" as="span" /></p>
                  </div>
                </div>
              </div>

              <div style={{ 
                background: 'var(--md-sys-color-surface-container)', 
                borderRadius: 'var(--radius-large)', 
                padding: '2rem',
                border: '1px solid var(--md-sys-color-outline-variant)'
              }}>
                <h4 style={{ marginBottom: '1rem' }}>What happens next?</h4>
                <ul className="service-features" style={{ fontSize: '0.95rem' }}>
                  <li>Our team reviews your inquiry within 24 hours</li>
                  <li>We'll schedule a call to discuss your needs</li>
                  <li>Receive a customized proposal</li>
                  <li>Quick onboarding to get you started</li>
                </ul>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
}

export default Contact;
